/**
 * Registro tipado de callables — sustituye a la lista manual de passthrough de la 1.0
 * (src/api/client.js, 43 KB; causa del bug "createListingBumpSession no implementada").
 *
 * Sin codegen: cada callable se declara UNA vez aquí con su nombre desplegado, el schema zod
 * de entrada y el tipo de salida. Functions valida la entrada con el schema antes de ejecutar
 * (`handle(def, fn)`); web/admin llaman con `call(def, data)` y reciben la salida tipada. Si el
 * front invoca algo que no existe en el registro, falla el typecheck, no el usuario.
 */
import type { z, ZodTypeAny } from 'zod';

export interface CallableDef<TIn extends ZodTypeAny, TOut> {
  /** Nombre DESPLEGADO: `${dominio}-${nombre}` (firebase agrupa `export const marketplace = {...}`). */
  readonly name: `${string}-${string}`;
  readonly domain: string;
  readonly input: TIn;
  /** Solo lleva el tipo; no se valida en runtime (lo produce el servidor). */
  readonly _output?: TOut;
  /** Requiere sesión iniciada (por defecto sí). */
  readonly auth: 'required' | 'optional' | 'none';
  /** Requiere custom claim admin. */
  readonly admin: boolean;
  /**
   * Se invoca a través de Firebase Hosting (`/api/<nombre>` → rewrite) en vez de la URL directa de
   * Cloud Functions, porque necesita las cabeceras que solo pone Hosting (`x-country-code`, 13b).
   * firebase.json debe tener el rewrite; `tools/check-rewrites.mjs` lo comprueba en CI.
   */
  readonly viaHosting: boolean;
}

export type CallableInput<D> = D extends CallableDef<infer I, unknown> ? z.input<I> : never;
export type CallableParsedInput<D> = D extends CallableDef<infer I, unknown> ? z.output<I> : never;
export type CallableOutput<D> = D extends CallableDef<ZodTypeAny, infer O> ? O : never;

export function defineCall<TIn extends ZodTypeAny, TOut>(
  domain: string,
  fn: string,
  input: TIn,
  opts: { auth?: 'required' | 'optional' | 'none'; admin?: boolean; viaHosting?: boolean } = {},
): CallableDef<TIn, TOut> {
  if (!/^[a-z][a-zA-Z0-9]*$/.test(domain) || !/^[a-z][a-zA-Z0-9]*$/.test(fn)) {
    throw new Error(`defineCall: nombre no válido "${domain}-${fn}" (camelCase, sin guiones)`);
  }
  return {
    name: `${domain}-${fn}`,
    domain,
    input,
    auth: opts.auth ?? 'required',
    admin: opts.admin ?? false,
    viaHosting: opts.viaHosting ?? false,
  };
}
